/* eslint-disable */
import Quill from 'quill';
import _ from 'lodash';

import i18n from '@/i18n';

const Module = Quill.import('core/module');

/**
 * A variable's visible name. Definitions carry an ENGLISH KEY as their label —
 * this is a quill module, not a component, so it translates through the app's
 * i18n instance at render time. Doing it here rather than in the configs keeps
 * the pills live when the locale changes, since the dropdown is rebuilt on every
 * open. Falls back to the placeholder key so an untranslated entry still reads.
 */
const variableLabel = (variable) => {
    const label = _.get(variable, 'label');

    return label ? i18n.t(label) : _.get(variable, 'value', '');
};

class PlaceholderVariableModule extends Module {
    constructor(quill, options) {
        super(quill, options);

        this.quill = quill;
        this.options = options;
        this.variables = [];
        this.dropdown = null;
        this.selectedIndex = 0;
        this.triggerIndex = null;
        this.isAutocomplete = false;
        this.savedSelection = null;

        this.initialize();

        // Register toolbar handler
        const toolbar = quill.getModule('toolbar');
        if (toolbar) {
            toolbar.addHandler('placeholder', this.showDropdown.bind(this));
        }

        // Listen for text changes to detect {{ trigger
        this.quill.on('text-change', this.onTextChange.bind(this));

        // Close dropdown on selection change or blur
        this.quill.on('selection-change', this.onSelectionChange.bind(this));

        // Handle keyboard navigation in dropdown
        this.quill.root.addEventListener('keydown', this.onKeyDown.bind(this));

        // Close on click outside - use mousedown to handle before focus changes
        this.boundOnMouseDown = this.onMouseDown.bind(this);
        document.addEventListener('mousedown', this.boundOnMouseDown);
    }

    initialize() {
        // Process variables from options
        if (this.options.variables) {
            if (typeof this.options.variables === 'function') {
                const result = this.options.variables();
                if (result instanceof Promise) {
                    result.then(variables => {
                        this.variables = variables || [];
                        this.updateToolbarButtonVisibility();
                    });
                } else {
                    this.variables = result || [];
                    this.updateToolbarButtonVisibility();
                }
            } else if (Array.isArray(this.options.variables)) {
                this.variables = this.options.variables;
                this.updateToolbarButtonVisibility();
            }
        } else {
            this.updateToolbarButtonVisibility();
        }
    }

    updateToolbarButtonVisibility() {
        const placeholderButton = this.quill.container.parentNode.querySelector('.ql-twig');
        if (placeholderButton) {
            const hasVariables = this.variables && this.variables.length > 0;
            // Hide the parent .ql-formats container, not just the button
            const formatsContainer = placeholderButton.closest('.ql-formats');
            if (formatsContainer) {
                formatsContainer.style.display = hasVariables ? '' : 'none';
            } else {
                placeholderButton.style.display = hasVariables ? '' : 'none';
            }
        }
    }

    onTextChange(delta, oldDelta, source) {
        if (source !== 'user') return;

        const selection = this.quill.getSelection();
        if (!selection) return;

        // Check for {{ trigger
        const cursorPosition = selection.index;
        const text = this.quill.getText(0, cursorPosition);

        // Find the last {{ that isn't closed
        const lastTriggerIndex = text.lastIndexOf('{{');
        if (lastTriggerIndex === -1) {
            this.hideDropdown();
            return;
        }

        // Check if there's a }} after the {{
        const textAfterTrigger = this.quill.getText(lastTriggerIndex, cursorPosition - lastTriggerIndex);
        if (textAfterTrigger.includes('}}')) {
            this.hideDropdown();
            return;
        }

        // Get the search text after {{
        const searchText = text.substring(lastTriggerIndex + 2).trim();

        // Show autocomplete dropdown
        this.triggerIndex = lastTriggerIndex;
        this.isAutocomplete = true;
        this.showDropdownAtCursor(searchText);
    }

    onSelectionChange(range) {
        // Don't close dropdown when focus moves to search input
        if (!range && this.dropdown && this.isAutocomplete) {
            this.hideDropdown();
        }
    }

    onKeyDown(event) {
        if (!this.dropdown) return;

        const filteredVars = this.getFilteredVariables();

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                this.selectedIndex = Math.min(this.selectedIndex + 1, filteredVars.length - 1);
                this.updateSelection();
                break;

            case 'ArrowUp':
                event.preventDefault();
                this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
                this.updateSelection();
                break;

            case 'Enter':
            case 'Tab':
                if (filteredVars.length > 0) {
                    event.preventDefault();
                    this.insertVariable(filteredVars[this.selectedIndex]);
                }
                break;

            case 'Escape':
                event.preventDefault();
                this.hideDropdown();
                break;
        }
    }

    onMouseDown(event) {
        if (!this.dropdown) return;

        // Check if click is inside dropdown
        if (this.dropdown.contains(event.target)) {
            return;
        }

        // Check if click is on toolbar placeholder button
        const placeholderButton = this.quill.container.parentNode.querySelector('.ql-twig');
        if (placeholderButton && placeholderButton.contains(event.target)) {
            return;
        }

        this.hideDropdown();
    }

    getFilteredVariables(searchText) {
        // Re-read variables from options each time (they may be set dynamically)
        let variables = this.variables;
        if (this.options.variables && Array.isArray(this.options.variables) && this.options.variables.length) {
            variables = this.options.variables;
        }

        const search = (searchText || this.currentSearch || '').toLowerCase();
        if (!search) return variables;

        return variables.filter(v => {
            const label = String(variableLabel(v)).toLowerCase();
            const value = _.get(v, 'value', '').toLowerCase();
            return label.includes(search) || value.includes(search);
        });
    }

    showDropdown() {
        // Toggle behavior - if dropdown is open, close it
        if (this.dropdown) {
            this.hideDropdown();
            return;
        }

        this.isAutocomplete = false;
        this.triggerIndex = null;

        // Focus editor if not focused and get/create selection
        this.quill.focus();
        let selection = this.quill.getSelection();
        if (!selection) {
            // Set cursor at end if no selection
            const length = this.quill.getLength();
            this.quill.setSelection(length - 1, 0);
            selection = this.quill.getSelection();
        }

        if (!selection) return;

        // Save selection before showing dropdown (focus will move to search input)
        this.savedSelection = selection;

        this.showDropdownAtCursor('');
    }

    showDropdownAtCursor(searchText) {
        this.currentSearch = searchText;
        this.selectedIndex = 0;

        const filteredVars = this.getFilteredVariables(searchText);

        if (filteredVars.length === 0) {
            this.hideDropdown();
            return;
        }

        // Create or update dropdown. It lives on document.body (not the editor container)
        // and is positioned fixed to the viewport, so it floats over everything and can
        // never expand or scroll a container it sits inside — the earlier absolute-in-
        // container approach blanked out the whole drawer when the caret was near the right
        // edge (a large offset scrolled the drawer to chase the off-screen dropdown).
        if (!this.dropdown) {
            this.dropdown = document.createElement('div');
            this.dropdown.id = 'placeholder-dropdown';
            document.body.appendChild(this.dropdown);
        }

        // Build dropdown content
        this.dropdown.innerHTML = '';

        // Add search input for toolbar dropdown
        if (!this.isAutocomplete) {
            const searchInput = document.createElement('input');
            searchInput.type = 'text';
            searchInput.className = 'placeholder-search';
            searchInput.placeholder = i18n.t(_.get(this.options, 'placeholder') || 'Search variable...');
            searchInput.value = searchText;
            searchInput.addEventListener('input', (e) => {
                this.currentSearch = e.target.value;
                this.renderOptions();
            });
            searchInput.addEventListener('keydown', (e) => {
                if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter') {
                    this.onKeyDown(e);
                }
            });
            this.dropdown.appendChild(searchInput);

            // Focus search input WITHOUT scrolling — a plain focus() would scroll the
            // nearest scroll container (e.g. the drawer body) to reveal it.
            setTimeout(() => searchInput.focus({ preventScroll: true }), 0);
        }

        // Add options container
        const optionsContainer = document.createElement('div');
        optionsContainer.className = 'placeholder-options';
        this.dropdown.appendChild(optionsContainer);

        this.renderOptions();

        // Position dropdown
        this.positionDropdown();
    }

    renderOptions() {
        const optionsContainer = this.dropdown.querySelector('.placeholder-options');
        if (!optionsContainer) return;

        const filteredVars = this.getFilteredVariables(this.currentSearch);
        optionsContainer.innerHTML = '';

        if (filteredVars.length === 0) {
            const emptyMsg = document.createElement('div');
            emptyMsg.className = 'placeholder-empty';
            emptyMsg.textContent = i18n.t('No variables found');
            optionsContainer.appendChild(emptyMsg);
            return;
        }

        filteredVars.forEach((variable, index) => {
            const option = document.createElement('div');
            option.className = 'placeholder-option';
            if (index === this.selectedIndex) {
                option.classList.add('active');
            }

            const label = document.createElement('span');
            label.className = 'placeholder-option-label';
            label.textContent = variableLabel(variable);

            const value = document.createElement('span');
            value.className = 'placeholder-option-value';
            value.textContent = '{{ ' + variable.value + ' }}';

            option.appendChild(label);
            option.appendChild(value);

            option.addEventListener('mousedown', (e) => {
                e.preventDefault(); // Prevent blur
                this.insertVariable(variable);
            });

            option.addEventListener('mouseenter', () => {
                this.selectedIndex = index;
                this.updateSelection();
            });

            optionsContainer.appendChild(option);
        });
    }

    updateSelection() {
        if (!this.dropdown) return;

        const options = this.dropdown.querySelectorAll('.placeholder-option');
        options.forEach((opt, index) => {
            if (index === this.selectedIndex) {
                opt.classList.add('active');
                opt.scrollIntoView({ block: 'nearest' });
            } else {
                opt.classList.remove('active');
            }
        });
    }

    positionDropdown() {
        if (!this.dropdown) return;

        const selection = this.quill.getSelection() || this.savedSelection;
        if (!selection) return;

        // The dropdown is position: fixed on document.body, so everything here is in
        // VIEWPORT coordinates: the caret bounds (editor-relative) plus the editor's own
        // viewport offset. Keeping a single coordinate system is what the old code got
        // wrong — it clamped a viewport width against a container-relative left.
        const bounds = this.quill.getBounds(selection.index);
        const editorRect = this.quill.root.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        let left = editorRect.left + bounds.left;
        let top = editorRect.top + bounds.top + bounds.height + 5;

        const dropdownRect = this.dropdown.getBoundingClientRect();

        // Keep it on-screen horizontally and vertically (flip above the caret if needed).
        if (left + dropdownRect.width > viewportWidth - 10) {
            left = Math.max(10, viewportWidth - dropdownRect.width - 10);
        }
        if (top + dropdownRect.height > viewportHeight - 10) {
            top = Math.max(10, editorRect.top + bounds.top - dropdownRect.height - 5);
        }

        this.dropdown.style.left = left + 'px';
        this.dropdown.style.top = top + 'px';
    }

    insertVariable(variable) {
        // Use saved selection for toolbar dropdown, current selection for autocomplete
        let selection = this.isAutocomplete ? this.quill.getSelection() : this.savedSelection;
        if (!selection) {
            this.hideDropdown();
            return;
        }

        let insertIndex = selection.index;
        let deleteLength = 0;

        // If autocomplete, remove the {{ trigger text
        if (this.isAutocomplete && this.triggerIndex !== null) {
            deleteLength = insertIndex - this.triggerIndex;
            insertIndex = this.triggerIndex;
        }

        // Focus editor before inserting
        this.quill.focus();

        // Delete trigger text if needed
        if (deleteLength > 0) {
            this.quill.deleteText(insertIndex, deleteLength, Quill.sources.USER);
        }

        // Insert the embed
        this.quill.insertEmbed(insertIndex, 'placeholder', variable.value, Quill.sources.USER);

        // Move cursor after the embed
        this.quill.setSelection(insertIndex + 1, 0, Quill.sources.USER);

        this.hideDropdown();
    }

    hideDropdown() {
        if (this.dropdown) {
            this.dropdown.remove();
            this.dropdown = null;
        }
        this.triggerIndex = null;
        this.isAutocomplete = false;
        this.currentSearch = '';
        this.selectedIndex = 0;
        this.savedSelection = null;
    }

    destroy() {
        document.removeEventListener('mousedown', this.boundOnMouseDown);
        this.hideDropdown();
    }
}

PlaceholderVariableModule.DEFAULTS = {
    variables: [],
    placeholder: 'Search variable...'
};

export default PlaceholderVariableModule;
